import { Grid } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, Outlet } from 'react-router-dom'

import { AlertText } from 'components/common/Alert'
import BusyOverlay from 'components/common/BusyOverlay'
import Button from 'components/common/Button'
import { Footer } from 'components/Footer'
import { LaunchDarklyProvider } from 'components/LaunchDarklyProvider'
import { NavDrawer } from 'components/navigation/Nav/NavDrawer'
import { NowRunningContextProvider } from 'components/now-running/contexts/NowRunningContext'
import { AppRefreshProvider } from 'context/AppRefreshContext'
import { MetadataProvider } from 'context/MetadataContext'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { group, identify } from 'track'
import { isPrivateEdition } from 'utils/app-config'
import { retryWithBackoff } from 'utils/retry'
import { NO_TEAM } from 'utils/session'

const SegmentIdentity: React.FC = () => {
  const {
    session,
    companyId,
    companyDisplayName,
    currentTeam,
    currentTeamId,
    roles
  } = useAuth()
  // this is a ref so we don't re-render when it changes
  const areAnalyticsReady = useRef(false)
  // however, we do want this one to trigger a re-render, so it's in state
  // the "dependencies" here are:
  //     window.analytics being loaded correctly (the .VERSION property should be available)
  //     session.identifier is available
  const [areDependenciesReady, setAreDependenciesReady] = useState(false)

  // we need to retry our check to run the analytics call since we're waiting on a third party library to load
  // and we want to be sure that the session data is available before it loads
  useEffect(() => {
    if (areAnalyticsReady.current) {
      return
    }

    // wait for analytics library to be loaded and session available before we make the identify call
    // if we don't do this, then we'll create excessive Monthly Tracked Users and increase our Segment bill ($$$)
    if (!areDependenciesReady) {
      retryWithBackoff(
        50,
        10 * 1000,
        () => window.analytics?.VERSION && session.identifier,
        () => setAreDependenciesReady(true)
      )

      return
    }

    window.analytics?.ready(() => {
      // Segment recommends not calling this for anon users
      identify(session.identifier, {
        email: session.identifier,
        firstName: session.first_name,
        lastName: session.last_name,
        companyId: companyId,
        companyName: companyDisplayName,
        companyRoles: session.roles,
        teamId: currentTeamId,
        teamName: currentTeam.name,
        roles
      })

      group(companyId, {
        email: session.identifier,
        companyId: companyId,
        companyName: companyDisplayName,
        companyRoles: session.roles,
        teamId: currentTeamId,
        teamName: currentTeam.name,
        roles: roles,
        userId: session.identifier
      })
      // ensure this is only ever called once
      areAnalyticsReady.current = true
    })
  }, [
    areAnalyticsReady,
    areDependenciesReady,
    companyDisplayName,
    companyId,
    currentTeam.name,
    currentTeamId,
    roles,
    session.first_name,
    session.identifier,
    session.last_name,
    session.roles
  ])

  return null
}

const CertificateExpirationContainer: React.FC = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const { currentTeamId, currentTeam } = useAuth()

  useEffect(() => {
    if (currentTeam?.certificate_expiration_imminent ?? false) {
      timeoutRef.current = setTimeout(() => {
        toast({
          type: 'error',
          text: (
            <AlertText>
              The current certificate will expire within 90 days.&nbsp;
            </AlertText>
          ),
          action: (
            <Button
              secondary
              as={Link}
              // @ts-ignore TODO: Either replace with Mui's Button or fix the types
              to={`/settings/team/${currentTeamId}/configuration`}
            >
              Update your configuration
            </Button>
          )
        })
      }, 1000)
    }

    return () => clearTimeout(timeoutRef.current)
  }, [currentTeam, currentTeamId])

  return null
}

export const AuthenticatedRoutesWrapper: React.FC = () => {
  const { currentTeamId, isRbacEnabled, ready } = useAuth()
  const userHasTeams = currentTeamId !== NO_TEAM

  return (
    <LaunchDarklyProvider isEnabled={!isPrivateEdition()}>
      <SegmentIdentity />
      <CertificateExpirationContainer />
      <MetadataProvider>
        <AppRefreshProvider>
          <NowRunningContextProvider>
            <Helmet />
            {isRbacEnabled && ready && (
              <NavDrawer userHasTeams={userHasTeams}>
                <Grid container>
                  <Grid
                    item
                    component="article"
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      marginTop: 'calc(var(--info-banner-height) * -1)',
                      paddingTop: 'var(--info-banner-height)'
                    }}
                    xs={12}
                  >
                    <Outlet />
                  </Grid>
                  <Grid
                    item
                    sx={{
                      marginTop: 'auto',
                      zIndex: 1
                    }}
                    xs={12}
                  >
                    <Footer />
                  </Grid>
                </Grid>
              </NavDrawer>
            )}
            <BusyOverlay shown={!isRbacEnabled || !ready} text="Loading..." />
          </NowRunningContextProvider>
        </AppRefreshProvider>
      </MetadataProvider>
    </LaunchDarklyProvider>
  )
}
